import { api } from "@config/axios/axios.config";
import { SERVER_ROUTES } from "@constants/api.routes";

export interface AttachmentResponse {
	url: string;
	success: boolean;
	message?: string;
}

/**
 * Fetches attachment URL by key
 * @param key - The attachment key from backend
 * @returns Promise<string> - The URL of the attachment
 */
export const getAttachmentUrl = async (key: string): Promise<string> => {
	try {
		// Encode the key properly to handle special characters and emojis
		// Split by '/' to encode each segment separately, then join
		const encodedKey = key
			.split("/")
			.map(segment => encodeURIComponent(segment))
			.join("/");

		const response = await api.get(SERVER_ROUTES.INSTITUTE.GET_ATTACHMENT_URL(encodedKey));
		
		// The axios response interceptor returns response?.data || response
		// The API returns: { success: boolean, statusCode: number, message: string, data: string }
		// After interceptor, response is: { success, statusCode, message, data: "url_string" }
		if (response && typeof response === 'object') {
			// Check if response has a data property (the actual URL string)
			if ('data' in response) {
				const url = (response as { data: unknown }).data;
				if (typeof url === 'string') {
					return url;
				}
			}
			// If response is the URL string directly (fallback)
			if (typeof response === 'string') {
				return response;
			}
		}
		
		// Fallback: return empty string if URL cannot be extracted
		console.warn("Could not extract URL from attachment response:", response);
		return "";
	} catch (error) {
		console.error("Error fetching attachment URL:", error);
		throw error;
	}
};

/**
 * Utility function to get attachment URL and handle errors
 * @param key - The attachment key
 * @returns Promise<string> - The attachment URL or empty string if failed
 */
export const getAttachmentUrlString = async (key: string) => {
	const result = await getAttachmentUrl(key);
	console.log("resultresultresult", result);
	return result;
};

/**
 * Utility function to check if attachment key is valid
 * @param key - The attachment key to validate
 * @returns boolean - True if key is valid (not empty, null, or undefined)
 */
export const isValidAttachmentKey = (key: string | null | undefined): boolean => {
	return Boolean(key && key.trim() !== "");
};

/**
 * Converts a file path to use the new attachment API endpoint
 * @param filePath - The file path from the backend (e.g., "uploads/file.jpg")
 * @returns string - The attachment key to use with the API
 */
export const convertFilePathToAttachmentKey = (filePath: string): string => {
	// Remove any leading slashes and return the path as the key
	return filePath.replace(/^\/+/, "");
};

/**
 * Gets the attachment URL for a file path using the new API
 * @param filePath - The file path from the backend
 * @returns Promise<string> - The actual file URL or empty string if failed
 */
export const getAttachmentUrlFromFilePath = async (filePath: string) => {
	if (!filePath) return "";

	const attachmentKey = convertFilePathToAttachmentKey(filePath);
	return await getAttachmentUrlString(attachmentKey);
};



export const getFullFileUrl = (filePath: string) => {
	if (!filePath) return "";

	// If the filePath is already a full URL (including blob URLs), return it as is
	if (filePath.startsWith("http://") || filePath.startsWith("https://") || filePath.startsWith("blob:")) {
		return filePath;
	}

	// For regular file paths, return as is (they might need to be processed by the attachment API)
	return filePath;
}

